<script>
import { defineComponent } from 'vue'
import { countryFlag } from '@/common/countryFlag'

export default defineComponent({
  name: 'MlbbTeamResultBlock',
  props: {
    name: {
      type: String,
      default: ''
    },
    rows: {
      type: Array,
      default: () => []
    },
    isWinner: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'red',
      validator: (v) => ['red', 'blue'].includes(v)
    },
    formatGold: {
      type: Function,
      default: (v) => String(v ?? 0)
    }
  },
  computed: {
    colorClass() {
      return `theme-${this.color}`
    }
  },
  methods: {
    countryFlag
  }
})
</script>

<template>
  <div :class="['mlbb-team-result-block', colorClass]">
    <div class="block-header">
      <p class="team-name">{{ name }}</p>
      <span v-if="isWinner" class="victory-badge">VICTORY</span>
      <div class="header-icons">
        <span class="header-icon" title="Золото">$</span>
        <span class="header-icon" title="Убийства">K</span>
        <span class="header-icon" title="Смерти">D</span>
        <span class="header-icon" title="Помощь">A</span>
      </div>
    </div>

    <div v-if="rows.length" class="block-rows">
      <div
        v-for="row in rows"
        :key="row.key"
        :class="['result-row', { 'not-played': !row.played }]"
      >
        <div class="hero-avatar">
          <img v-if="row.avatar" :src="row.avatar" :alt="row.heroName || row.nick" />
          <div v-else class="avatar-fallback">{{ (row.nick || '?').charAt(0).toUpperCase() }}</div>
          <span v-if="row.played" class="level-badge">{{ row.level != null ? row.level : 0 }}</span>
        </div>

        <div class="player-info">
          <p class="player-nick">
            <span v-if="countryFlag(row.countryCode)"
                  class="player-flag"
                  :title="row.countryName || row.countryCode">{{ countryFlag(row.countryCode) }}</span>
            {{ row.nick }}
          </p>
        </div>

        <div class="player-gold">
          <template v-if="row.played">{{ formatGold(row.gold) }} $</template>
          <template v-else>—</template>
        </div>
        <div class="player-stat">{{ row.played ? (row.kills != null ? row.kills : 0) : '—' }}</div>
        <div class="player-stat">{{ row.played ? (row.deaths != null ? row.deaths : 0) : '—' }}</div>
        <div class="player-stat">{{ row.played ? (row.assists != null ? row.assists : 0) : '—' }}</div>

        <div class="player-mvp">
          <span v-if="row.isMvp" class="mvp-badge">MVP</span>
        </div>
      </div>
    </div>
    <p v-else class="no-data">Нет данных по игрокам</p>
  </div>
</template>

<style scoped lang="scss">
.mlbb-team-result-block {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &.theme-red {
    --card-bg: #2c0d12;
    --header-bg: #4a1018;
    --row-divider: rgba(255, 90, 110, 0.12);
    --team-name-color: #ffd2d8;
  }

  &.theme-blue {
    --card-bg: #0d1b3a;
    --header-bg: #122a59;
    --row-divider: rgba(90, 161, 255, 0.12);
    --team-name-color: #c9dcff;
  }

  background: var(--card-bg);

  .block-header {
    background: var(--header-bg);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;

    .team-name {
      margin: 0;
      font-weight: 700;
      color: var(--team-name-color);
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .victory-badge {
      font-size: 10px;
      font-weight: 800;
      color: #5a3d00;
      background: #f6c453;
      padding: 2px 8px;
      border-radius: 6px;
      letter-spacing: 1px;
    }

    .header-icons {
      display: flex;
      gap: 14px;
      align-items: center;

      .header-icon {
        width: 18px;
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.5);
        letter-spacing: 0.5px;
      }
    }
  }

  .block-rows {
    .result-row {
      display: grid;
      grid-template-columns: 60px 1fr 80px 28px 28px 28px 40px;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-top: 1px solid var(--row-divider);

      &:first-child {
        border-top: none;
      }

      &.not-played {
        opacity: 0.55;
      }

      .hero-avatar {
        position: relative;
        width: 48px;
        height: 48px;

        img {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .avatar-fallback {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 18px;
        }

        .level-badge {
          position: absolute;
          bottom: -2px;
          left: -2px;
          background: #11151f;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          padding: 1px 6px;
          border-radius: 4px;
        }
      }

      .player-info {
        min-width: 0;

        .player-nick {
          margin: 0;
          color: #fff;
          font-weight: 600;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          line-height: 1.2;

          .player-flag {
            margin-right: 4px;
          }
        }
      }

      .player-gold {
        text-align: right;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        color: rgba(255, 255, 255, 0.85);
      }

      .player-stat {
        text-align: center;
        font-weight: 700;
        color: #fff;
        font-variant-numeric: tabular-nums;
      }

      .player-mvp {
        display: flex;
        justify-content: flex-end;

        .mvp-badge {
          font-size: 10px;
          font-weight: 800;
          color: #5a3d00;
          background: #f6c453;
          padding: 2px 6px;
          border-radius: 6px;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .no-data {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    margin: 0;
    padding: 12px 16px;
  }
}
</style>
